/**
 * OwnerAdminOperationsHealth — single-pane-of-glass health view for
 * the founder / operator (Eli). Built after the Feb 2026 data-drift
 * incident: the goal is to catch operational issues BEFORE customers
 * find them.
 *
 * Powered by `GET /api/owner-admin/dashboard/operations-health` which
 * aggregates: service health pings, MRR/ARR, 30-day funnel, prioritized
 * action items, and recent error counts.
 */
import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Activity, AlertTriangle, CheckCircle2, RefreshCw, ChevronRight,
  DollarSign, TrendingUp, Users, ListChecks, ShieldAlert,
} from 'lucide-react';
import { apiFetch } from '../../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_LABELS = {
  mongo:  'Database',
  plaid:  'Plaid sync',
  stripe: 'Stripe / Billing',
  llm:    'AI (Claude / GPT)',
  email:  'Resend / Email',
};

const SERVICE_TONES = {
  ok:      { dot: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-300', label: 'Healthy' },
  idle:    { dot: 'bg-zinc-400',    text: 'text-zinc-600 dark:text-zinc-400',       label: 'Idle 24h' },
  down:    { dot: 'bg-rose-500',    text: 'text-rose-700 dark:text-rose-300',       label: 'Down' },
  unknown: { dot: 'bg-amber-500',   text: 'text-amber-700 dark:text-amber-300',     label: 'Unknown' },
};

const SEVERITY_BADGE = {
  high:   'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30',
  medium: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
  low:    'bg-zinc-500/10 text-zinc-700 dark:text-zinc-300 border-zinc-500/30',
};

export default function OwnerAdminOperationsHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const r = await apiFetch(`${API_URL}/api/owner-admin/dashboard/operations-health`);
      if (r.ok) setData(await r.json());
    } catch {
      // soft fail — UI shows last good payload if any
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const refresh = () => { setRefreshing(true); load(); };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-6">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading operations health…
      </div>
    );
  }

  if (!data) {
    return <p className="p-6 text-sm text-muted-foreground">Could not load operations health.</p>;
  }

  const services = Object.entries(data.service_health || {});
  const items = data.action_items || [];
  const allGood = items.length === 0;

  return (
    <div className="space-y-6" data-testid="owner-admin-operations-health">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Activity className="w-5 h-5 text-emerald-500" />
            Operations Health
          </h2>
          <p className="text-sm text-muted-foreground">
            Live status of every service powering CountBucks, plus your prioritized to-do list.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={refresh}
            disabled={refreshing}
            data-testid="ops-health-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={async () => {
              try {
                const r = await apiFetch(
                  `${API_URL}/api/owner-admin/dashboard/operations-health/send-digest-now`,
                  { method: 'POST' },
                );
                if (r.ok) {
                  const j = await r.json();
                  if (j.sent) {
                    alert(`Morning digest sent to: ${(j.recipients || []).join(', ')}`);
                  } else {
                    alert(`Send failed: ${j.error || 'unknown error'}`);
                  }
                } else {
                  alert('Send failed');
                }
              } catch {
                alert('Send failed');
              }
            }}
            data-testid="ops-health-send-digest-now"
          >
            Send digest now
          </Button>
        </div>
      </div>

      {/* Service health row */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-indigo-500" />
            Service status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {services.map(([key, val]) => {
              const tone = SERVICE_TONES[val.status] || SERVICE_TONES.unknown;
              return (
                <div
                  key={key}
                  className="rounded-lg border bg-card p-3 flex flex-col gap-1.5"
                  data-testid={`ops-health-service-${key}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${tone.dot}`} />
                    <span className="text-xs font-medium">{SERVICE_LABELS[key] || key}</span>
                  </div>
                  <span className={`text-[11px] font-semibold ${tone.text}`}>
                    {tone.label}
                  </span>
                  {typeof val.count_24h === 'number' && val.count_24h > 0 && (
                    <span className="text-[10px] text-muted-foreground">
                      {val.count_24h.toLocaleString()} events / 24h
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Revenue + Funnel */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card data-testid="ops-health-revenue">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-emerald-500" />
              Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Metric label="MRR" value={`$${(data.revenue.mrr_usd || 0).toLocaleString()}`} tone="emerald" />
              <Metric label="ARR" value={`$${(data.revenue.arr_usd || 0).toLocaleString()}`} tone="emerald" />
              <Metric label="Active subs" value={(data.revenue.active_subscriptions || 0).toLocaleString()} tone="indigo" />
              <Metric
                label="Plaid floor"
                value={`$${(data.plaid_usage?.floor_per_month_usd || 0).toLocaleString()}/mo`}
                tone="rose"
              />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="ops-health-funnel">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-500" />
              Funnel · last 30 days
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-3">
              <Metric label="Signups"        value={data.funnel_30d.signups || 0} tone="blue" />
              <Metric label="Activations"    value={data.funnel_30d.activations || 0} tone="emerald" />
              <Metric label="Cancellations"  value={data.funnel_30d.cancellations || 0} tone="rose" />
              <Metric
                label="Net"
                value={data.funnel_30d.net_growth || 0}
                tone={(data.funnel_30d.net_growth || 0) >= 0 ? 'emerald' : 'rose'}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action items */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-amber-500" />
            What you need to do
            {!allGood && (
              <Badge variant="outline" className="ml-1 text-[10px]">{items.length} open</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {allGood ? (
            <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="w-4 h-4" />
              All systems operational. No action items.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="ops-health-action-items">
              {items.map((it, idx) => (
                <li
                  key={idx}
                  className="flex items-center justify-between gap-3 rounded-lg border bg-card px-3 py-2.5"
                  data-testid={`ops-health-action-${it.category}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Badge
                      variant="outline"
                      className={`text-[10px] uppercase ${SEVERITY_BADGE[it.severity] || SEVERITY_BADGE.low}`}
                    >
                      {it.severity}
                    </Badge>
                    <span className="text-sm">{it.message}</span>
                  </div>
                  {it.link && (
                    <a
                      href={it.link}
                      className="text-xs text-primary hover:underline inline-flex items-center gap-1 whitespace-nowrap"
                    >
                      Open <ChevronRight className="w-3 h-3" />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {data.alerts_24h > 0 && (
        <div className="text-[11px] text-muted-foreground flex items-center gap-1">
          <AlertTriangle className="w-3 h-3 text-amber-500" />
          {data.alerts_24h} error-flavored audit-log events in the last 24h.
        </div>
      )}

      {/* Smart Alert banner — surfaces when Plaid spend growth is
          outpacing MRR growth over the last 7 days. Idempotent on the
          email side; the banner is informational only. */}
      <SmartAlertBanner />

      {/* Preview → Prod deploy diff banner — surfaces when preview has
          commits ahead of prod, so fixes don't sit un-deployed for days.
          Polls /deploy-status, fetches PROD /api/version cross-origin. */}
      <DeployDiffBanner />

      {/* 30-day MRR vs Plaid floor trend — visual check that Plaid spend
          isn't outpacing revenue. Populated nightly by the daily admin
          digest service. */}
      <MetricsTrendCard />

      <p className="text-[10px] text-muted-foreground">
        Refreshed {new Date(data.generated_at).toLocaleString()}
      </p>
    </div>
  );
}

function Metric({ label, value, tone = 'emerald' }) {
  const toneClasses = {
    emerald: 'text-emerald-700 dark:text-emerald-300',
    rose:    'text-rose-700 dark:text-rose-300',
    indigo:  'text-indigo-700 dark:text-indigo-300',
    blue:    'text-blue-700 dark:text-blue-300',
  }[tone] || 'text-foreground';
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className={`text-lg font-semibold ${toneClasses}`}>{value}</span>
    </div>
  );
}

/** 30-day MRR vs Plaid floor cost sparkline. Tiny inline SVG (no chart
    lib dep needed) — both series normalized to the same vertical axis so
    you can visually spot when Plaid spend pulls level with MRR. */
function MetricsTrendCard() {
  const [points, setPoints] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/owner-admin/dashboard/metrics-trend?days=30`);
        if (r.ok) setPoints((await r.json()).points || []);
      } catch { setPoints([]); }
    })();
  }, []);
  if (points === null) return null;
  if (points.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-indigo-500" />MRR vs Plaid floor · 30 days
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground">
            No snapshots captured yet. The morning digest writes a daily row;
            check back after the 07:00 UTC tick (or click &ldquo;Send digest now&rdquo; above to capture today&apos;s row).
          </p>
        </CardContent>
      </Card>
    );
  }
  const maxVal = Math.max(1, ...points.map(p => Math.max(p.mrr_usd || 0, p.plaid_floor_usd || 0)));
  const W = 600, H = 80;
  const pt = (i, v) => `${(i / Math.max(1, points.length - 1)) * W},${H - (v / maxVal) * H}`;
  const mrrPath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${pt(i, p.mrr_usd || 0)}`).join(' ');
  const plaidPath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${pt(i, p.plaid_floor_usd || 0)}`).join(' ');
  const latest = points[points.length - 1] || {};
  return (
    <Card data-testid="ops-health-metrics-trend">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-indigo-500" />MRR vs Plaid floor · 30 days
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4 mb-2 text-[11px]">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-0.5 bg-emerald-500" /> MRR
            <span className="font-semibold text-emerald-700 dark:text-emerald-300 ml-1">
              ${(latest.mrr_usd || 0).toLocaleString()}
            </span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-0.5 bg-rose-500" /> Plaid floor
            <span className="font-semibold text-rose-700 dark:text-rose-300 ml-1">
              ${(latest.plaid_floor_usd || 0).toLocaleString()}
            </span>
          </span>
        </div>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-20" preserveAspectRatio="none" aria-hidden>
          <path d={mrrPath} stroke="#10b981" strokeWidth="2" fill="none" />
          <path d={plaidPath} stroke="#f43f5e" strokeWidth="2" fill="none" />
        </svg>
        <p className="text-[10px] text-muted-foreground mt-2">
          {points.length} day{points.length === 1 ? '' : 's'} of snapshots
        </p>
      </CardContent>
    </Card>
  );
}


/** SmartAlertBanner — surfaces the Plaid-spend-vs-MRR slope alert.
 *  Only renders when the backend marks `is_on=true`. Includes a quick
 *  "Re-evaluate now" action that bypasses the nightly schedule. */
function SmartAlertBanner() {
  const [state, setState] = useState(null);
  const [evaluating, setEvaluating] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await apiFetch(`${API_URL}/api/owner-admin/smart-alerts`);
      if (r.ok) setState(await r.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const evaluateNow = async () => {
    setEvaluating(true);
    try {
      const r = await apiFetch(`${API_URL}/api/owner-admin/smart-alerts/evaluate-now`, { method: 'POST' });
      if (r.ok) setState(await r.json());
    } catch { /* ignore */ }
    setEvaluating(false);
  };

  if (!state || !state.is_on) return null;

  return (
    <Card
      data-testid="smart-alert-banner"
      className="border-rose-500/40 bg-rose-500/5"
    >
      <CardContent className="p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-rose-700 dark:text-rose-300 text-sm">
            Plaid spend is outpacing MRR
          </p>
          <p className="text-xs text-rose-700/80 dark:text-rose-300/80 mt-0.5 leading-relaxed">
            Over the last <strong>{state.days_evaluated} days</strong>, Plaid
            spend slope <strong>${(state.plaid_slope_per_day ?? 0).toFixed(2)}/day</strong> vs.
            MRR slope <strong>${(state.mrr_slope_per_day ?? 0).toFixed(2)}/day</strong>{' '}
            (ratio <strong>{(state.ratio ?? 0).toFixed(2)}x</strong>). Check trial users
            with multiple bank connections and dormant accounts holding Plaid items.
          </p>
        </div>
        <button
          type="button"
          onClick={evaluateNow}
          disabled={evaluating}
          data-testid="smart-alert-evaluate-now-btn"
          className="text-xs font-semibold uppercase tracking-wider bg-rose-500/20 hover:bg-rose-500/30 text-rose-700 dark:text-rose-300 px-3 py-1.5 rounded-md whitespace-nowrap"
        >
          {evaluating ? 'Checking...' : 'Re-check now'}
        </button>
      </CardContent>
    </Card>
  );
}


/** DeployDiffBanner — surfaces "X commits ready, last deploy N hours
 *  ago" when preview has un-deployed code. Hides itself when up-to-date.
 *  Polls every 60s so a freshly-merged fix shows up quickly. */
function DeployDiffBanner() {
  const [state, setState] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await apiFetch(`${API_URL}/api/owner-admin/deploy-status`);
      if (r.ok) setState(await r.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  if (!state || !state.available || state.up_to_date) return null;
  const ahead = state.commits_ahead ?? 0;
  if (ahead < 1) return null;

  const hours = state.hours_since_prod_deploy;
  const lastDeployText = hours == null
    ? 'unknown'
    : hours < 24
      ? `${hours}h ago`
      : `${Math.floor(hours / 24)}d ago`;

  // Color escalates with staleness: 1-2 commits = info, 3-5 = warn,
  // 6+ OR >72h = urgent. Use full class names (no template strings)
  // because Tailwind's JIT only compiles classes it can statically see.
  const urgent = ahead >= 6 || (hours != null && hours >= 72);
  const warn = !urgent && (ahead >= 3 || (hours != null && hours >= 24));
  const styles = urgent
    ? {
        border: 'border-rose-500/40',
        bg: 'bg-rose-500/5',
        icon: 'text-rose-500',
        title: 'text-rose-700 dark:text-rose-300',
        body: 'text-rose-700/80 dark:text-rose-300/80',
        link: 'text-rose-700 dark:text-rose-300',
      }
    : warn
      ? {
          border: 'border-amber-500/40',
          bg: 'bg-amber-500/5',
          icon: 'text-amber-500',
          title: 'text-amber-700 dark:text-amber-300',
          body: 'text-amber-700/80 dark:text-amber-300/80',
          link: 'text-amber-700 dark:text-amber-300',
        }
      : {
          border: 'border-sky-500/40',
          bg: 'bg-sky-500/5',
          icon: 'text-sky-500',
          title: 'text-sky-700 dark:text-sky-300',
          body: 'text-sky-700/80 dark:text-sky-300/80',
          link: 'text-sky-700 dark:text-sky-300',
        };

  return (
    <Card
      data-testid="deploy-diff-banner"
      className={`${styles.border} ${styles.bg}`}
    >
      <CardContent className="p-4 flex items-start gap-3">
        <AlertTriangle className={`w-5 h-5 ${styles.icon} flex-shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <p className={`font-semibold ${styles.title} text-sm`} data-testid="deploy-diff-headline">
            {ahead} fix{ahead === 1 ? '' : 'es'} ready to deploy
            <span className="opacity-70 font-normal"> · last deploy {lastDeployText}</span>
          </p>
          <p className={`text-xs ${styles.body} mt-0.5 leading-relaxed`}>
            Preview is at <span className="font-mono">{state.local?.commit}</span>,
            production is at <span className="font-mono">{state.prod?.commit}</span>.
            Push to <span className="font-mono">countbucks.com</span> via the Emergent
            dashboard to make these live.
          </p>
          {state.pending_subjects?.length > 0 && (
            <details className="mt-2 text-[11px]">
              <summary className={`cursor-pointer ${styles.link} hover:underline`}>
                Show pending commits
              </summary>
              <ul className="mt-1 ml-2 space-y-0.5 font-mono text-muted-foreground">
                {state.pending_subjects.slice(0, 8).map((s, i) => (
                  <li key={i} className="truncate">{s}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

